import { Category } from '@/api/types/categories.types';
import { simplifyStr } from '@/utils/helpers';

export const findCategory = (
  categories: Category[] | undefined,
  currentPath: string
): Category | null => {
  if (!categories) return null;

  for (const category of categories) {
    if (category.path === currentPath) {
      return category;
    }
    if (category.children) {
      const found = findCategory(category.children, currentPath);
      if (found) {
        return found;
      }
    }
  }
  return null;
};

function sortByName(a: Category, b: Category): number {
  return simplifyStr(a.name).localeCompare(simplifyStr(b.name));
}

export function findSubcategoriesByName(
  query: string,
  categories: Category[]
): { title: string; data: Category[] }[] {
  const simpleValue = simplifyStr(query);
  const data: {
    title: string;
    topItems: Category[];
    otherItems: Category[];
  }[] = [];

  categories.forEach(category => {
    const topItems: Category[] = [];
    const otherItems: Category[] = [];

    category.children?.forEach((subCategory: Category) => {
      const names = [
        simplifyStr(subCategory.name),
        subCategory.azName ? simplifyStr(subCategory.azName) : '',
        subCategory.ruName ? simplifyStr(subCategory.ruName) : '',
      ].filter(Boolean);

      const matchesQuery = names.some(name => name.includes(simpleValue));
      const startsWithQuery = names.some(name => name.startsWith(simpleValue));

      if (matchesQuery) {
        if (startsWithQuery) {
          topItems.push(subCategory);
        } else {
          otherItems.push(subCategory);
        }
      }
    });

    if (topItems.length + otherItems.length === 0) return;

    topItems.sort(sortByName);
    otherItems.sort(sortByName);

    data.push({ title: category.name, topItems, otherItems });
  });

  data.sort((a, b) => {
    if (a.topItems.length > 0 && b.topItems.length > 0) {
      if (simplifyStr(a.topItems[0].name) < simplifyStr(b.topItems[0].name))
        return -1;
      if (simplifyStr(a.topItems[0].name) > simplifyStr(b.topItems[0].name))
        return 1;
    }
    if (a.topItems.length > b.topItems.length) return -1;
    if (a.topItems.length < b.topItems.length) return 1;

    if (a.otherItems.length > 0 && b.otherItems.length > 0) {
      if (simplifyStr(a.otherItems[0].name) < simplifyStr(b.otherItems[0].name))
        return -1;
      if (simplifyStr(a.otherItems[0].name) > simplifyStr(b.otherItems[0].name))
        return 1;
    }

    return 0;
  });

  return data.map(({ title, topItems, otherItems }) => ({
    title,
    data: topItems.concat(otherItems),
  }));
}
